import React, { useEffect, useRef } from 'react';
import { ChatMessage } from '../../types/chat';
import ChatHeader from './ChatHeader';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import ChatInput from './ChatInput';
import SupportCaptureForm from './SupportCaptureForm';
import ConversationTabs from './ConversationTabs';
import { SupportIcon } from './icons/ChatIcons';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOffline } from '../../contexts/OfflineContext';
import { Link } from 'react-router-dom';

// Palabras clave que indican que el error es de límite de presupuesto
const BUDGET_ERROR_KEYWORDS = [
  'límite de uso gratuito',
  'BUDGET_LIMIT_EXCEEDED',
  'presupuesto',
  'plan de pago',
  'soporte para actualizar'
];

interface ChatWindowProps {
  isOpen: boolean;
  messages: ChatMessage[];
  isTyping: boolean;
  onClose: () => void;
  onSendMessage: (content: string) => void;
  onNewChat?: () => void;
  title?: string;
  autoSpeakEnabled?: boolean;
  autoSpeakActivatedAt?: number | null;
  onToggleAutoSpeak?: () => void;
  error?: string | null;
  onRetry?: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  messages,
  isTyping,
  onClose,
  onSendMessage,
  onNewChat,
  title,
  autoSpeakEnabled,
  autoSpeakActivatedAt,
  onToggleAutoSpeak,
  error,
  onRetry,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Visitante = sin sesión: el asistente responde en modo restringido (solo sobre
  // la app) y lo invita a registrarse. El backend detecta "sin token" = visitor.
  const { user } = useAuth();
  const isVisitor = !user;
  const {
    ttsMethod,
    budgetWarning,
    clearBudgetWarning,
    budgetLimitExceeded,
    cancelSpeech,
    submitFeedback,
    supportStatus,
    supportAdminTyping,
    requestHandoffCapture,
    startHandoff,
    cancelHandoff,
    closeHandoff,
    conversations,
    activeConversationId,
    createConversation,
    selectConversation,
    closeConversation,
    pendingCount,
    sendVoiceNote,
  } = useChat();
  // Cola offline (KAN-218) + notas de voz (KAN-225): sin conexión el micrófono
  // graba una nota que se transcribe con OpenAI al reconectar (la Web Speech
  // API no transcribe offline). Los visitantes no pueden transcribir (el
  // endpoint exige cuenta): para ellos el micrófono queda deshabilitado
  // offline.
  const { isOnline } = useOffline();

  // Botón "Hablar con una persona" del header:
  // - Visitor: abre el form de captura (name + email + mensaje inicial) que al
  //   submitir dispara startHandoff(msg, name, email).
  // - Logueado: arranca el handoff directo con un mensaje por defecto (el
  //   server valida min(1) en initialMessage); el usuario continúa escribiendo
  //   en el input normal, que ya rutea a sendSupportMessage vía sendMessage.
  const handleStartHandoff = () => {
    if (isVisitor) {
      requestHandoffCapture();
    } else {
      // Logueado: mensaje por defecto corto. El contexto empuja este texto
      // al historial (pushUser) para que el usuario vea su "consulta inicial".
      void startHandoff('Hola, necesito hablar con una persona.');
    }
  };

  // Submit del SupportCaptureForm (sólo visitor). Llama a startHandoff con el
  // mensaje capturado + name + email.
  const handleCaptureSubmit = (name: string, email: string, message: string) => {
    void startHandoff(message, name, email);
  };

  // Cerrar la atención al cliente → arrancar una conversación NUEVA con la IA
  // (mismo efecto que tocar "+"). Cierra el hilo del lado server, resetea el
  // estado de soporte y limpia el historial para empezar de cero.
  const handleCloseHandoff = async () => {
    await closeHandoff();
    onNewChat?.();
  };

  // Verificar si el error es de límite de presupuesto
  const isBudgetError = (errorMsg: string | null | undefined) => {
    if (!errorMsg) return false;
    return BUDGET_ERROR_KEYWORDS.some(keyword =>
      errorMsg.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  // Calcular el índice del último mensaje del asistente
  const lastAssistantMessageIndex = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return i;
      }
    }
    return -1;
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Al abrir el chat, ir directo al último mensaje (instantáneo, sin animación)
  // para que el usuario vea el final de la conversación en lugar del principio.
  // El efecto de arriba solo depende de `messages`/`isTyping`, así que al abrir
  // (cuando el historial no cambia) no disparaba y la vista quedaba arriba. (KAN-177)
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 right-6 w-[calc(100%-3rem)] sm:w-[34rem] h-[90vh] bg-paper rounded-lg shadow-overlay flex flex-col z-50 border border-hairline">
      <ChatHeader
        onClose={onClose}
        onNewChat={onNewChat}
        title={title}
        autoSpeakEnabled={autoSpeakEnabled}
        onToggleAutoSpeak={onToggleAutoSpeak}
        ttsMethod={ttsMethod}
        budgetWarning={budgetWarning}
        onClearBudgetWarning={clearBudgetWarning}
        supportStatus={supportStatus}
        onStartHandoff={handleStartHandoff}
        onCloseHandoff={handleCloseHandoff}
      />

      {/* Pestañas de conversaciones (KAN-218): hasta 4, intercambiables */}
      <ConversationTabs
        conversations={conversations}
        activeId={activeConversationId}
        onSelect={selectConversation}
        onClose={closeConversation}
        onNew={createConversation}
      />

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-2 bg-paper-2/30"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="bg-success/10 p-4 rounded-lg mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-forest"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
            <h4 className="text-ink font-display font-medium mb-2">
              ¡Hola! Soy el asistente de MiCampoDigital
            </h4>
            <p className="text-sm text-sage">
              {isVisitor
                ? 'Te cuento cómo funciona la app y qué puede hacer por tu campo. Para consultar tu propio rodeo y tus números, creá tu cuenta.'
                : 'Puedo ayudarte con preguntas sobre gestión ganadera, uso de la aplicación y más.'}
            </p>
            {isVisitor && (
              <Link
                to="/login"
                className="mt-3 inline-block text-xs font-semibold text-paper bg-forest hover:bg-forest-mid px-3 py-1.5 rounded-lg transition-colors"
              >
                Crear cuenta gratis →
              </Link>
            )}
            {supportStatus === 'idle' && (
              <button
                type="button"
                onClick={handleStartHandoff}
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-paper bg-forest hover:bg-forest-mid px-4 py-2 rounded-lg transition-colors shadow-card"
                title="Hablar con atención al cliente"
              >
                <SupportIcon className="h-4 w-4" />
                Hablar con atención al cliente
              </button>
            )}
            <div className="mt-4 space-y-2 text-left w-full">
              <p className="text-xs text-sage font-medium">Ejemplos:</p>
              {(isVisitor
                ? // Visitante (sin cuenta, modo restringido): solo responde sobre la app.
                  [
                    '¿Qué puede hacer la app por mi campo?',
                    '¿Cómo cargo mis animales?',
                  ]
                : // Usuario logueado: puede consultar su rodeo y sus números.
                  [
                    '¿Cómo está la preñez de mi rodeo?',
                    '¿Cuántos animales tengo por categoría?',
                    '¿Cómo me fue financieramente este año?',
                    '¿A cuánto está el ganado hoy?',
                  ]
              ).map((ejemplo) => (
                <button
                  key={ejemplo}
                  onClick={() => onSendMessage(ejemplo)}
                  className="w-full text-left text-sm bg-paper border border-hairline rounded-lg px-3 py-2 text-ink hover:bg-paper-2 hover:border-forest/40 transition-colors"
                >
                  {ejemplo}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                autoSpeakEnabled={autoSpeakEnabled}
                autoSpeakActivatedAt={autoSpeakActivatedAt}
                isLastMessage={index === lastAssistantMessageIndex}
                onFeedback={user ? (thumb, text) => submitFeedback(message.id, thumb, text) : undefined}
              />
            ))}
            {(isTyping || supportAdminTyping) && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Support capture form (visitor sin sesión): pide name + email + mensaje
          inicial antes de disparar el handoff. Reemplaza el input normal mientras
          el usuario está cargando sus datos. */}
      {supportStatus === 'capture' && (
        <SupportCaptureForm
          onSubmit={handleCaptureSubmit}
          onCancel={cancelHandoff}
        />
      )}

      {/* Error Banner - Entre mensajes y input */}
      {error && (
        <div className={`mx-4 mb-2 p-3 border-l-4 rounded-r-lg ${
          isBudgetError(error) || budgetLimitExceeded
            ? 'bg-warning/10 border-warning'
            : 'bg-danger/10 border-danger'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2 flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                isBudgetError(error) || budgetLimitExceeded
                  ? 'text-warning'
                  : 'text-danger'
              }`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className={`text-xs font-semibold mb-1 ${
                  isBudgetError(error) || budgetLimitExceeded
                    ? 'text-warning'
                    : 'text-danger'
                }`}>
                  {isBudgetError(error) || budgetLimitExceeded
                    ? 'Límite de uso alcanzado'
                    : 'Error de conexión'
                  }
                </h4>
                <p className={`text-xs ${
                  isBudgetError(error) || budgetLimitExceeded
                    ? 'text-warning'
                    : 'text-danger'
                }`}>
                  {error}
                </p>
                {(isBudgetError(error) || budgetLimitExceeded) && (
                  <p className="text-xs text-warning mt-1">
                    💡 Escribile a soporte para ampliar tu plan
                  </p>
                )}
              </div>
            </div>
            {onRetry && !isBudgetError(error) && !budgetLimitExceeded && (
              <button
                onClick={onRetry}
                className="ml-2 px-2 py-1 bg-danger text-paper text-xs rounded-lg hover:bg-danger/90 transition-colors"
              >
                Reintentar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Banner offline: los mensajes quedan encolados y salen solos al
          reconectar (KAN-218). Informativo — no es un error. */}
      {!isOnline && pendingCount > 0 && (
        <div className="px-4 py-2 border-t border-hairline flex items-center gap-2 text-xs bg-info/10 text-ink">
          <span className="inline-flex h-2 w-2 rounded-pixel bg-info animate-pulse flex-shrink-0" />
          <span className="flex-1">
            Sin conexión — {pendingCount} {pendingCount === 1 ? 'mensaje se enviará' : 'mensajes se enviarán'} automáticamente al reconectar.
          </span>
        </div>
      )}

      {/* Banner de estado del handoff: feedback persistente para el usuario
          mientras espera o está siendo atendido. Sobrevive a recargas (se basa
          en supportStatus, no en un mensaje del chat) así que cubre también el
          recovery, donde no vuelve a llegar el evento handoff:queued/live. */}
      {(supportStatus === 'queued' || supportStatus === 'live' || supportStatus === 'requesting') && (
        <div className={`px-4 py-2 border-t border-hairline flex items-center gap-2 text-xs ${
          supportStatus === 'live'
            ? 'bg-success/10 text-ink'
            : supportStatus === 'queued'
              ? 'bg-warning/10 text-ink'
              : 'bg-info/10 text-ink'
        }`}>
          <span className={`inline-flex h-2 w-2 rounded-pixel flex-shrink-0 ${
            supportStatus === 'live'
              ? 'bg-success'
              : 'bg-warning animate-pulse'
          }`} />
          <span className="flex-1">
            {supportStatus === 'live'
              ? '🟢 Estás conectado con el equipo. Escribí tu mensaje abajo.'
              : supportStatus === 'queued'
                ? 'Tu mensaje fue enviado. Alguien del equipo lo va a ver y responder a la brevedad.'
                : 'Conectando con el equipo…'}
          </span>
        </div>
      )}

      {/* Input Area — deshabilitado mientras el visitor llena el form de
          captura o el handoff está solicitando (todavía no hay hilo activo). */}
      <ChatInput
        onSend={onSendMessage}
        disabled={
          isTyping ||
          (isBudgetError(error) || !!budgetLimitExceeded) ||
          supportStatus === 'capture' ||
          supportStatus === 'requesting'
        }
        autoSpeakEnabled={autoSpeakEnabled}
        onToggleAutoSpeak={onToggleAutoSpeak}
        onCancelSpeech={cancelSpeech}
        micDisabled={!isOnline && isVisitor}
        micDisabledHint="El dictado necesita conexión"
        voiceNoteMode={!isOnline && !isVisitor}
        onVoiceNote={(note) => { void sendVoiceNote(note); }}
      />
    </div>
  );
};

export default ChatWindow;
